import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Download, Trash2 } from "lucide-react";

export default function Profile() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [deleting, setDeleting] = useState(false);

  const exportData = async () => {
    try {
      const { data } = await api.get("/account/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "brewmate-data.json";
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Your data has been exported");
    } catch { toast.error("Could not export data"); }
  };

  const deleteAccount = async () => {
    setDeleting(true);
    try {
      await api.post("/account/delete");
      toast.success("Account deleted");
      setUser(null);
      navigate("/login", { replace: true });
    } catch { toast.error("Could not delete account"); setDeleting(false); }
  };

  return (
    <div data-testid="profile-page">
      <PageHeader title="Manage Profile" onBack={() => navigate("/account")} />
      <div className="px-5 pt-5 space-y-5 pb-8">
        <div className="rounded-2xl border border-[#EDE0CC]/20 bg-[#0D2875] p-4 space-y-3">
          <Row label="Name" value={user?.name} />
          <Row label="Email" value={user?.email} />
          <p className="text-xs text-[#EDE0CC]/50">Your name and email come from your Wonderbean Shopify account. Update them in your Wonderbean account settings.</p>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/60 mb-2">Your data</div>
          <Button onClick={exportData} data-testid="export-data-btn" variant="outline" className="w-full h-12 rounded-full border-[#EDE0CC]/40 text-[#EDE0CC] hover:bg-[#EDE0CC]/10 flex items-center justify-center gap-2">
            <Download size={18} /> Export recipes & preferences
          </Button>
        </div>

        <div>
          <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/60 mb-2">Account actions</div>
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button data-testid="delete-account-btn" className="w-full h-12 rounded-full bg-[#E56B6B] text-white hover:bg-[#d95a5a] flex items-center justify-center gap-2">
                <Trash2 size={18} /> Delete Account
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="bg-[#0D2875] border-[#EDE0CC]/20 text-[#EDE0CC]">
              <AlertDialogHeader>
                <AlertDialogTitle className="text-[#EDE0CC]">Delete your account?</AlertDialogTitle>
                <AlertDialogDescription className="text-[#EDE0CC]/70">
                  This permanently deletes your saved recipes, notes and preferences, and signs you out. This cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full border-[#EDE0CC]/40 bg-transparent text-[#EDE0CC] hover:bg-[#EDE0CC]/10">Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={deleteAccount} disabled={deleting} data-testid="confirm-delete-account-btn" className="rounded-full bg-[#E56B6B] text-white hover:bg-[#d95a5a]">
                  {deleting ? "Deleting…" : "Delete Account"}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-[#EDE0CC]/50">{label}</div>
      <div className="mt-0.5 text-[#EDE0CC]">{value}</div>
    </div>
  );
}
